import React, { useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { getAPIEntityValues } from '~/jobiqo/utils/config';
import Button from '~/vcl/atoms/01-forms/01-button';
import { Drawer } from '~/vcl/molecules/04-navigation/02-drawer';
import { FieldGroupInfo } from '~/jobiqo/models/forms';
import JobDetailPanel from '../../../Detail';
import { Job } from '~/jobiqo/types/types';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { EyeIcon } from '~/vcl/icons/Eye';
import { CloseIcon } from '~/vcl/icons/Close';
import styles from './jobPreview.module.scss';
import { FormattedMessage } from 'react-intl';
import { useConfig } from '~/jobiqo/context/config';
import { useIsMobile } from '~/vcl/hooks/useIsMobile';
import { cva } from 'class-variance-authority';
import FlexItems from '~/vcl/atoms/06-layout/03-flex';
import HiddenVisually from '~/vcl/atoms/14-w-a11y/02-visually-hidden';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const jobPreviewContainer = cva(
  [styles.jobPreviewContainer, 'job_preview_container'],
  {
    variants: {
      isMobile: {
        true: styles.jobPreviewContainerMobile,
      },
    },
  },
);

const jobPreviewButton = cva([styles.jobPreviewButton, 'job_preview_button'], {
  variants: {
    isMobile: {
      true: styles.jobPreviewButtonMobile,
      false: styles.jobPreviewButtonDesktop,
    },
  },
});

interface JobPreviewProps {
  /**
   * The job type.
   */
  jobType: string;
  /**
   * If the job is being edited.
   */
  isEdit: boolean;
}

export function JobPreview({ jobType }: JobPreviewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { getValues } = useFormContext();
  const { JOBS } = useFrontendConfig();
  const FIELD_GROUPS: FieldGroupInfo[] = Object.values(JOBS.JOB_DETAIL_FIELDS);
  const { user, currentOrganizationProfile } = useContext<UserContextInterface>(
    UserContext,
  );
  const isMobile = useIsMobile();
  const { getConfigByName } = useConfig();
  const JOB_PREVIEW_ENABLED: boolean = !!getConfigByName('job_preview_enabled')
    ?.value[0];

  const job = getAPIEntityValues(FIELD_GROUPS, getValues(), true);

  if (jobType === 'job_per_file' || !JOB_PREVIEW_ENABLED) {
    // We do not show the preview button for job_per_file or
    // if the job preview is not enabled.
    return null;
  }

  // Convert single terms into arrays because that's how we return them from the API.
  // We have to replicate the API.
  Object.keys(job as Job).forEach((fieldKey) => {
    if (job[fieldKey]?.tid && !Array.isArray(job[fieldKey])) {
      (job as Job)[fieldKey] = [job[fieldKey]];
    }
  });

  return (
    <div className={styles.button}>
      <Button
        type="button"
        bordered={!isMobile}
        rounded={isMobile}
        iconed={isMobile}
        fab={isMobile}
        buttonStyle="primary"
        className={jobPreviewButton({ isMobile })}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen(true);
        }}
      >
        <EyeIcon width={24} height={24} />
        {isMobile ? (
          <HiddenVisually>
            <FormattedMessage
              id="job_preview_button"
              defaultMessage="Preview"
            />
          </HiddenVisually>
        ) : (
          <span>
            <FormattedMessage
              id="job_preview_button"
              defaultMessage="Preview"
            />
          </span>
        )}
      </Button>
      <Drawer
        maxWidth={isMobile ? '90%' : '800px'}
        full={true}
        position="right"
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
      >
        {isOpen && (
          <FlexItems justifyContent="flex-end">
            <Button dense type="button" onClick={() => setIsOpen(false)}>
              <CloseIcon height={20} width={20} />
            </Button>
          </FlexItems>
        )}
        <div className={jobPreviewContainer({ isMobile })}>
          <JobDetailPanel
            user={user}
            fullContainer={true}
            overflow={true}
            isPreview={true}
            job={
              {
                ...job,
                type: jobType,
                organization:
                  currentOrganizationProfile?.name || user?.organization?.name,
                organizationProfile: currentOrganizationProfile,
                workflowState: { value: 'published' },
                url: {
                  path: '/job/preview',
                },
              } as Job
            }
          />
        </div>
      </Drawer>
    </div>
  );
}
